import {
  PaginatedTable,
  IPagedResultsV1,
  Column,
  TableCellExtras,
  Icon,
  createTableColumn,
  createTableCell,
} from '@gcl/react-core';
import * as React from 'react';
import { TableContentLoader } from '@common/TableContentLoader';
import {
  IProjectTransmittalDistributionDisplay,
  IProjectTransmittalHistory,
} from '@transmittal/history/transmittalHistory.models';
import { TRANSMITTAL_ITEM_REVISION_COLUMN_WIDTH } from '@common/constants';
import { TransmittalHistoryTableLayout } from '../../TransmittalHistoryTableLayout';

export interface ITransmittalDistributionTableProps {
  transmittalHistoriesPagedResult?: IPagedResultsV1<IProjectTransmittalHistory> | undefined;
  isLoading: boolean;
  hoverTransmittalId: number | undefined;
  setHoverTransmittalId: React.Dispatch<React.SetStateAction<number | undefined>>;
}

export const TransmittalDistributionTable: React.FunctionComponent<ITransmittalDistributionTableProps> = ({
  transmittalHistoriesPagedResult,
  isLoading = true,
  hoverTransmittalId,
  setHoverTransmittalId,
}) => {
  const distributionList: IProjectTransmittalDistributionDisplay[] = React.useMemo(() => {
    if (!transmittalHistoriesPagedResult?.items || transmittalHistoriesPagedResult.items.length <= 0)
      return [] as IProjectTransmittalDistributionDisplay[];

    const temp: { [key: string]: IProjectTransmittalDistributionDisplay } = {};

    transmittalHistoriesPagedResult.items.forEach((transmittalHistory) => {
      transmittalHistory.distributions.forEach((distributionUser) => {
        temp[`${distributionUser.displayName}_${distributionUser.type}`] = distributionUser;
      });
    });

    return Object.values(temp);
  }, [JSON.stringify(transmittalHistoriesPagedResult)]);

  const defaultColumns: Column[] = React.useMemo(() => {
    return [
      createTableColumn({
        disableSortBy: true,
        accessor: 'id',
        Header: () => {
          return (
            <div className="gcl-gems-transmittal-history-page-item-cols">
              <div>Distribution</div>
              <div>Attention</div>
            </div>
          );
        },
        disableResizing: true,
        Cell: createTableCell<number, IProjectTransmittalDistributionDisplay>(
          (
            value: number,
            item: IProjectTransmittalDistributionDisplay,
            index: number,
            cellExtras: { [key: string]: any },
          ) => {
            const displayNameArr = item.displayName.split('-');
            return (
              <div className="gcl-gems-transmittal-history-page-item-cols">
                <div>
                  <>
                    {displayNameArr[0]}
                    <br />
                    {displayNameArr[1] ? `- ${displayNameArr[1]}` : undefined}
                  </>
                </div>
                <div>
                  {item.memberNames.map((memberName) => (
                    <>
                      {memberName}
                      <br />
                    </>
                  ))}
                </div>
              </div>
            );
          },
        ),
      } as Column),
    ];
  }, []);
  const [columns, setColumns] = React.useState<Column[]>([...defaultColumns]);

  React.useEffect(() => {
    if (isLoading) return;

    if (
      !transmittalHistoriesPagedResult ||
      !transmittalHistoriesPagedResult.items ||
      transmittalHistoriesPagedResult.items.length <= 0
    ) {
      setColumns([...defaultColumns]);
      return;
    }

    const tempColumns: Column[] = (transmittalHistoriesPagedResult?.items || []).map((transmittalHistory) => {
      return {
        ...createTableColumn({
          Header: '',
          accessor: transmittalHistory.id.toString(),
          disableResizing: true,
          disableSortBy: true,
          width: TRANSMITTAL_ITEM_REVISION_COLUMN_WIDTH,
          maxWidth: TRANSMITTAL_ITEM_REVISION_COLUMN_WIDTH,
          minWidth: TRANSMITTAL_ITEM_REVISION_COLUMN_WIDTH,
          Cell: createTableCell<string, any>(
            (
              value: string,
              item: IProjectTransmittalDistributionDisplay,
              index: number,
              cellExtras: { [key: string]: any },
            ) => {
              if (
                !transmittalHistory.distributions.find(
                  (d) => d.displayName === item.displayName && d.type === item.type,
                )
              )
                return <></>;
              return <Icon iconName="Check" />;
            },
          ),
        } as Column),
        id: transmittalHistory.id.toString(),
      } as Column;
    });
    setColumns([...defaultColumns, ...tempColumns]);
  }, [JSON.stringify(transmittalHistoriesPagedResult)]);

  if (isLoading) return <TableContentLoader isLoading={isLoading} />;

  return (
    <TableContentLoader isLoading={isLoading}>
      <TransmittalHistoryTableLayout
        mainTable={
          <div
            className="gcl-gems-transmittal-item-table"
            onMouseLeave={() => {
              setHoverTransmittalId(undefined);
            }}
          >
            <PaginatedTable
              columns={columns}
              items={distributionList}
              hoverHighlight
              disablePagination
              allowExpandRow={false}
              allowSelectRow={false}
              responsive={false}
              classNames={{
                dataCell: (item: object | undefined, column: Column, cellExtras: TableCellExtras | undefined) => {
                  if (
                    column.id &&
                    Number(column.id?.trim()) &&
                    Number(column.id?.trim()) === cellExtras?.hoverTransmittalId
                  )
                    return 'gcl-gems-transmittal-history-page-table-cell-highlight';
                  return '';
                },
              }}
              cellExtras={{ hoverTransmittalId, setHoverTransmittalId }}
              eventProps={{
                dataCell: {
                  onMouseEnter: (item: object | undefined, column: Column, cellExtras: TableCellExtras | undefined) => {
                    if (column.id && Number(column.id?.trim()))
                      cellExtras?.setHoverTransmittalId(Number(column.id?.trim()));
                    else cellExtras?.setHoverTransmittalId(undefined);
                  },
                },
              }}
            />
          </div>
        }
        pkgTable={<></>}
      />
    </TableContentLoader>
  );
};
